/**
 * Authority Setup Wizard - Multi-step wizard for setting up a new authority
 *
 * This wizard is used by the first employee when setting up an authority.
 * It generates the Authority Encryption Key (AEK) and Group Key Distribution Key (GKDK).
 */

import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useCryptoKeyStore, generateKeyId } from '@antragsmanager/crypto-lib';
import { createAuthorityWithKey } from '../../api/keysApi';

interface AuthoritySetupWizardProps {
  open: boolean;
  onClose: () => void;
  onComplete: (authorityId: string) => void;
}

const steps = ['Behörde benennen', 'Schlüssel generieren', 'Abschließen'];

export function AuthoritySetupWizard({
  open,
  onClose,
  onComplete,
}: AuthoritySetupWizardProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [authorityName, setAuthorityName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdAuthorityId, setCreatedAuthorityId] = useState<string | null>(null);

  const { activeKey, initializeAuthority } = useCryptoKeyStore();

  const handleNext = useCallback(async () => {
    if (activeStep === 0) {
      // Validate authority name
      if (!authorityName.trim()) {
        setError('Bitte geben Sie einen Namen für die Behörde ein');
        return;
      }
      setError(null);
      setActiveStep(1);
    } else if (activeStep === 1) {
      // Generate keys and create authority
      if (!activeKey) {
        setError('Kein aktiver Schlüssel vorhanden. Bitte melden Sie sich zuerst an.');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const authorityId = generateKeyId();

        // Initialize authority (creates AEK + GKDK locally)
        const bundle = await initializeAuthority(authorityId, authorityName.trim());

        // Register authority with backend
        await createAuthorityWithKey({
          authorityId,
          authorityName: authorityName.trim(),
          publicKeyJwk: bundle.aekPublicKeyJwk,
          fingerprint: bundle.aekFingerprint,
          creatorKeyId: activeKey.keyId,
          creatorName: activeKey.ownerName || 'Unbekannt',
          creatorPublicKeyJwk: activeKey.publicKeyJwk,
          creatorFingerprint: activeKey.fingerprint,
        });

        setCreatedAuthorityId(authorityId);
        setActiveStep(2);
      } catch (err) {
        console.error('Failed to create authority:', err);
        setError(
          err instanceof Error
            ? err.message
            : 'Fehler beim Erstellen der Behörde. Bitte versuchen Sie es erneut.'
        );
      } finally {
        setIsLoading(false);
      }
    } else if (activeStep === 2) {
      // Complete
      if (createdAuthorityId) {
        onComplete(createdAuthorityId);
      }
      handleClose();
    }
  }, [activeStep, authorityName, activeKey, initializeAuthority, createdAuthorityId, onComplete]);

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError(null);
  };

  const handleClose = () => {
    setActiveStep(0);
    setAuthorityName('');
    setError(null);
    setCreatedAuthorityId(null);
    onClose();
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" gutterBottom>
              Geben Sie den Namen Ihrer Behörde oder Organisation ein.
            </Typography>
            <TextField
              fullWidth
              label="Name der Behörde"
              value={authorityName}
              onChange={(e) => setAuthorityName(e.target.value)}
              margin="normal"
              autoFocus
              placeholder="z.B. Stadtverwaltung Musterstadt"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Als erster Mitarbeiter werden Sie automatisch als Administrator dieser Behörde
              registriert und können weitere Mitarbeiter einladen.
            </Typography>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            {isLoading ? (
              <>
                <CircularProgress size={48} sx={{ mb: 2 }} />
                <Typography variant="body1">
                  Verschlüsselungsschlüssel werden generiert...
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Dies kann einige Sekunden dauern.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body1" gutterBottom>
                  Klicken Sie auf &quot;Weiter&quot;, um die Verschlüsselungsschlüssel für Ihre
                  Behörde zu generieren.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Es werden ein Authority Encryption Key (AEK) und ein Group Key Distribution
                  Key (GKDK) generiert. Diese ermöglichen die Ende-zu-Ende-Verschlüsselung
                  von Bürgeranträgen.
                </Typography>
              </>
            )}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="success.main" gutterBottom>
              Behörde erfolgreich eingerichtet!
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Die Behörde &quot;{authorityName}&quot; wurde erfolgreich eingerichtet.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sie können nun weitere Mitarbeiter einladen, indem Sie ihnen einen
              Onboarding-QR-Code zur Verfügung stellen.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Neue Behörde einrichten</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Abbrechen
        </Button>
        {activeStep > 0 && activeStep < 2 && (
          <Button onClick={handleBack} disabled={isLoading}>
            Zurück
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={isLoading || (activeStep === 0 && !authorityName.trim())}
        >
          {activeStep === 2 ? 'Fertig' : 'Weiter'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
